/* ============================================================================
   JuiceBox Limited VIP 2026 — behaviour for sections/jb-vip-hero, jb-vip-prize,
   jb-vip-perks, jb-vip-steps, jb-vip-cta.

   Modules: countdowns + the four-stage phase, Klaviyo signup (multi-instance, e-mail only),
   scroll reveals, the Sorten filter, the password-page gate (numeric keypad over the native
   storefront_password form). The initial state is server-rendered by Liquid (digits,
   phase, and — for the two secret stages — the markup itself); this file only keeps it live.
   Loaded (deferred) by several sections, so it guards against running twice.

   Stages (snippets/jb-vip-phase): teaser → prize (01.10. 18:00) → reveal (02.10. 18:00) → open
   (10.10. 10:00). Crossing INTO prize or reveal on an open tab needs new server markup (the car /
   the flavours are deliberately absent before), so the page reloads itself once — only when the
   tab is visible, no form field has focus, and not twice within 3 min (edge-cache lag). Crossing
   into open is a pure CSS toggle on data-jb-vip-phase, as before.

   Klaviyo: browser → POST https://a.klaviyo.com/client/subscriptions/?company_id=…
   (public company id, 202 Accepted). Same contract as snippets/wm-tippen-form.liquid.

   House rules kept here: no element carries aria-controls (theme.js delegates a click
   on every [aria-controls] to a dialog toggle), helpers are jbVip-prefixed, nothing is
   put on window except the init guard.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__jbVipInit) return;
  window.__jbVipInit = true;

  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  function jbVipPad(n) { return (n < 10 ? '0' : '') + n; }

  /* ==================== countdowns + phase ==================== */

  var PHASES = ['teaser', 'prize', 'reveal', 'open'];
  var cdTimer = null;

  var JB_VIP_SIGNUP_OFF = 30 * 60; // password page: sign-up ends 30 min before the launch (also in sections/jb-vip-hero)

  function jbVipEpoch(el, attr) {
    var v = parseInt(el.getAttribute(attr), 10);
    return (isFinite(v) && v > 0) ? v : Infinity;
  }

  function jbVipPhaseIdx(root) {
    var now = Date.now() / 1000;
    var idx = 0;
    if (now >= jbVipEpoch(root, 'data-jb-vip-prize')) idx = 1;
    if (now >= jbVipEpoch(root, 'data-jb-vip-reveal')) idx = 2;
    if (now >= jbVipEpoch(root, 'data-jb-vip-launch')) idx = 3;
    return idx;
  }

  // One soft reload per stage, and only when it cannot interrupt anyone. Without sessionStorage
  // (private mode) we never reload: a stale edge cache would otherwise loop.
  function jbVipMaybeReload(stage) {
    if (document.hidden) return false;
    var ae = document.activeElement;
    if (ae && /^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName)) return false;
    var key = 'jbVipReload:' + stage, last = 0;
    try { last = parseInt(sessionStorage.getItem(key) || '0', 10) || 0; } catch (e) { return false; }
    if (Date.now() - last < 180000) return false;
    try { sessionStorage.setItem(key, String(Date.now())); } catch (e) { return false; }
    window.location.reload();
    return true;
  }

  function jbVipTick() {
    var pending = false;

    // digits: every countdown ticks towards its own epoch
    Array.prototype.forEach.call(document.querySelectorAll('[data-jb-vip-countdown-to]'), function (cd) {
      var endMs = jbVipEpoch(cd, 'data-jb-vip-countdown-to') * 1000;
      if (!isFinite(endMs)) return;
      var diff = Math.floor((endMs - Date.now()) / 1000);
      var d = 0, h = 0, m = 0, sec = 0;
      if (diff > 0) {
        pending = true;
        d = Math.floor(diff / 86400);
        h = Math.floor((diff % 86400) / 3600);
        m = Math.floor((diff % 3600) / 60);
        sec = diff % 60;
      }
      var cells = { d: jbVipPad(d), h: jbVipPad(h), m: jbVipPad(m), s: jbVipPad(sec) };
      Array.prototype.forEach.call(cd.querySelectorAll('[data-jb-vip-cd]'), function (el) {
        var v = cells[el.getAttribute('data-jb-vip-cd')];
        if (v !== undefined && el.textContent !== v) el.textContent = v;
      });
      if (m !== cd.__jbVipMinute) {
        cd.__jbVipMinute = m;
        var what = cd.getAttribute('data-jb-vip-cd-what') || 'bis der Tresor öffnet';
        Array.prototype.forEach.call(cd.querySelectorAll('[data-jb-vip-timer]'), function (t) {
          t.setAttribute('aria-label', 'Noch ' + d + ' Tage, ' + h + ' Stunden und ' + m + ' Minuten ' + what);
        });
      }
    });

    // phase: compare what the server rendered with what the clock says
    var reloading = false;
    Array.prototype.forEach.call(document.querySelectorAll('[data-jb-vip-root]'), function (root) {
      var rendered = PHASES.indexOf(root.getAttribute('data-jb-vip-phase'));
      if (rendered < 0) rendered = 2;             // unknown / legacy value: never trigger a reload from it
      var idx = jbVipPhaseIdx(root);
      if (idx < 3) pending = true;
      if (idx <= rendered) return;
      if (rendered < 2 && !reloading) {
        // the car (stage 2) or the flavours (stage 3) are not in this DOM — fetch a fresh render
        reloading = jbVipMaybeReload(PHASES[Math.min(idx, 2)]);
        if (reloading) return;
      }
      if (idx === 3 && root.getAttribute('data-jb-vip-phase') !== 'open') {
        root.setAttribute('data-jb-vip-phase', 'open');
      }
    });

    // page-wide switch for texts outside a root (password page: invite, steps, perks, FAQ — classes jb-vip-pg-before /
    // jb-vip-pg-open in assets/jb-vip.css). Flips with the hero at 10:00, also after the console preview trick.
    // Password page (keypad hero) only: already JB_VIP_SIGNUP_OFF seconds BEFORE the launch (merchant 2026-10-10: no
    // sign-up from 09:30) — the signup form and "Jetzt VIP werden" buttons go, the "ab So, 11.10." texts come.
    var pageOpen = Array.prototype.some.call(document.querySelectorAll('[data-jb-vip-root]'), function (r) {
      if (r.getAttribute('data-jb-vip-phase') === 'open') return true;
      return !!r.querySelector('.jb-vip-open--keypad') && Date.now() / 1000 >= jbVipEpoch(r, 'data-jb-vip-launch') - JB_VIP_SIGNUP_OFF;
    });
    document.documentElement.setAttribute('data-jb-vip-page', pageOpen ? 'open' : 'before');

    if (!pending && cdTimer) { clearInterval(cdTimer); cdTimer = null; }
  }

  jbVipTick();
  cdTimer = setInterval(jbVipTick, 1000);
  // Background tabs throttle timers; re-tick when the tab comes back so a visitor
  // returning after a stage change sees the right state, not a stale countdown.
  document.addEventListener('visibilitychange', function () { if (!document.hidden) jbVipTick(); });

  /* ==================== car reveal (prize section) ==================== */

  var reveals = document.querySelectorAll('[data-jb-vip-reveal]');
  if (reveals.length) {
    if (reduceMotion || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(reveals, function (el) { el.classList.add('is-revealed'); });
    } else {
      // Default: light up once 35 % is in view. An element can ask for more via
      // data-jb-vip-reveal-at (the car waits until it is almost fully on screen) or less
      // (the tall Sorten grids). The root is extended 4000px UPWARDS so an element a fast
      // scroll or a jump has already passed counts as fully intersecting and lights up too —
      // otherwise a skipped element would stay invisible until the visitor scrolls back.
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          var need = parseFloat(e.target.getAttribute('data-jb-vip-reveal-at')) || 0.35;
          if (e.isIntersecting && e.intersectionRatio >= need) {
            e.target.classList.add('is-revealed');
            io.unobserve(e.target);
          }
        });
      }, { threshold: [0.05, 0.15, 0.35, 0.6, 0.9, 1], rootMargin: '4000px 0px 0px 0px' });
      Array.prototype.forEach.call(reveals, function (el) { io.observe(el); });
    }
  }

  /* ==================== Sorten filter (stage 3) ==================== */

  Array.prototype.forEach.call(document.querySelectorAll('[data-jb-vip-sorten]'), function (root) {
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-jb-vip-card]'));
    var grid = root.querySelector('[data-jb-vip-grid]');
    var lineBtns = Array.prototype.slice.call(root.querySelectorAll('[data-jb-vip-line]'));
    var catBtns = Array.prototype.slice.call(root.querySelectorAll('[data-jb-vip-cat]'));
    var count = root.querySelector('[data-jb-vip-count]');
    var reset = root.querySelector('[data-jb-vip-reset]');
    var empty = root.querySelector('[data-jb-vip-empty]');
    if (!cards.length || !grid) return;

    var state = { line: 'all', cat: 'all' };

    function apply(animate) {
      var shown = 0;
      cards.forEach(function (card) {
        var line = card.getAttribute('data-line') || '';
        var cats = ' ' + (card.getAttribute('data-cats') || '') + ' ';
        var ok = (state.line === 'all' || line === state.line) &&
                 (state.cat === 'all' || cats.indexOf(' ' + state.cat + ' ') !== -1);
        card.hidden = !ok;
        if (ok) { card.style.setProperty('--i', String(shown)); shown++; }
      });
      lineBtns.forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-jb-vip-line') === state.line ? 'true' : 'false'); });
      catBtns.forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-jb-vip-cat') === state.cat ? 'true' : 'false'); });
      if (count) count.textContent = String(shown);
      if (reset) reset.hidden = (state.line === 'all' && state.cat === 'all');
      if (empty) empty.hidden = shown > 0;
      if (animate && !reduceMotion && grid.classList.contains('is-revealed')) {
        // restart the stagger for the cards that are now visible
        grid.classList.remove('is-revealed');
        void grid.offsetWidth;
        grid.classList.add('is-revealed');
      }
    }

    lineBtns.forEach(function (b) {
      b.addEventListener('click', function () { state.line = b.getAttribute('data-jb-vip-line') || 'all'; apply(true); });
    });
    catBtns.forEach(function (b) {
      b.addEventListener('click', function () { state.cat = b.getAttribute('data-jb-vip-cat') || 'all'; apply(true); });
    });
    if (reset) reset.addEventListener('click', function () { state.line = 'all'; state.cat = 'all'; apply(true); });

    apply(false);
  });

  /* ==================== the gate: storefront password as a safe ==================== */

  Array.prototype.forEach.call(document.querySelectorAll('[data-jb-vip-gate]'), function (gate) {
    var form = gate.closest('form');
    var input = gate.querySelector('[data-jb-vip-code]');
    var slots = Array.prototype.slice.call(gate.querySelectorAll('[data-jb-vip-slot]'));
    var keys = Array.prototype.slice.call(gate.querySelectorAll('[data-jb-vip-key]'));
    var submitBtn = gate.querySelector('[data-jb-vip-gate-submit]');
    if (!form || !input) return;

    var len = parseInt(gate.getAttribute('data-jb-vip-gate-len'), 10) || 0;
    var sending = false;

    function clean(v) { return String(v || '').replace(/\D+/g, '').slice(0, len || 64); }
    function render() {
      var n = input.value.length;
      gate.classList.toggle('is-typing', n > 0);
      slots.forEach(function (sl, i) {
        sl.classList.toggle('is-filled', i < n);
        sl.classList.toggle('is-active', i === n || (n >= slots.length && i === slots.length - 1));
      });
    }
    // phones (merchant 2026-10-10): a finger on the website keypad must NOT pop the phone keyboard — the pad closes it;
    // only a tap on the code slots (the field lies on top of them) opens the number keyboard. Mouse/desktop: as before.
    var lastPointer = 'mouse';
    function focusInput() {
      if (lastPointer === 'touch' || lastPointer === 'pen') { if (document.activeElement === input) input.blur(); return; }
      try { input.focus({ preventScroll: true }); } catch (e) { try { input.focus(); } catch (e2) {} }
    }
    function visible() { return gate.offsetParent !== null; }
    function go() {
      if (sending) return;
      if (!input.value.length) { focusInput(); return; }
      sending = true;
      gate.classList.add('is-sending');
      if (submitBtn) submitBtn.disabled = true;
      // the native form does the work: POST /password → Shopify sets the cookie and redirects
      if (typeof form.requestSubmit === 'function') { form.requestSubmit(); return; }
      // older Safari (< 16) has no requestSubmit, and form.submit() fires no submit event — announce it ourselves so
      // the submit listeners (the Limited-page note in snippets/jb-vip-keypad) still run
      var ev;
      try { ev = new Event('submit', { bubbles: true, cancelable: true }); } catch (e) { ev = null; }
      if (ev && !form.dispatchEvent(ev)) { sending = false; gate.classList.remove('is-sending'); if (submitBtn) submitBtn.disabled = false; return; }
      form.submit();
    }
    function add(d) {
      if (sending) return;
      var next = clean(input.value + d);
      if (next === input.value) return;
      input.value = next;
      render();
      if (len && next.length >= len) { setTimeout(go, 140); } else { focusInput(); }
    }
    function del() {
      if (sending) return;
      input.value = input.value.slice(0, -1);
      render();
      focusInput();
    }

    // progressive enhancement: from here on the pad + slots are the UI; the field stays focusable
    // (physical keyboards keep working) but never pops the on-screen keyboard.
    gate.classList.add('is-enhanced');
    input.setAttribute('inputmode', 'none');
    // the field goes on top of the slots: tapping the slots IS tapping the field (the only reliable way to get the
    // phone keyboard on iOS). The display loses aria-hidden so the field stays reachable for screen readers.
    var display = gate.querySelector('.jb-vip-gate__display');
    if (display) {
      display.appendChild(input);
      display.removeAttribute('aria-hidden');
      Array.prototype.forEach.call(display.querySelectorAll('[data-jb-vip-slot]'), function (sl) { sl.setAttribute('aria-hidden', 'true'); });
    }
    input.addEventListener('pointerdown', function (e) {
      lastPointer = e.pointerType || 'mouse';
      if (lastPointer !== 'mouse') input.setAttribute('inputmode', 'numeric'); // before focus → number keyboard
    });
    input.addEventListener('blur', function () { input.setAttribute('inputmode', 'none'); });
    gate.addEventListener('pointerdown', function (e) {
      if (e.target !== input) lastPointer = e.pointerType || 'mouse';
    }, true);
    input.setAttribute('aria-label', (gate.querySelector('.jb-vip-gate__label') || {}).textContent || 'Zugangscode');
    if (gate.classList.contains('is-error')) input.value = '';
    render();

    keys.forEach(function (k) {
      k.addEventListener('click', function () {
        var v = k.getAttribute('data-jb-vip-key');
        if (v === 'del') del(); else if (v === 'go') go(); else add(v);
      });
    });

    input.addEventListener('input', function () {
      var v = clean(input.value);
      if (v !== input.value) input.value = v;
      render();
      if (len && v.length >= len) setTimeout(go, 140);
    });

    // typing anywhere on the page while the gate is on screen goes into the safe
    document.addEventListener('keydown', function (e) {
      if (!visible() || sending || e.metaKey || e.ctrlKey || e.altKey) return;
      var t = e.target;
      // the Sorten-Finder (snippets/jb-vip-finder) has its own keys: Enter on its buttons, ←/→ in the deck — never the safe
      if (t && t.closest && t.closest('[data-jb-vip-finder]')) return;
      if (document.querySelector('dialog[data-jb-vip-finder][open]')) return;
      var inField = t && (t === input);
      var inOther = t && t !== input && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName || '');
      if (inOther) return;
      if (/^[0-9]$/.test(e.key)) { if (!inField) { e.preventDefault(); add(e.key); } return; }
      if (e.key === 'Backspace') { if (!inField) { e.preventDefault(); del(); } return; }
      if (e.key === 'Enter' && !inField) { e.preventDefault(); go(); }
    });

    form.addEventListener('submit', function (e) {
      if (!input.value.length) { e.preventDefault(); focusInput(); return; }
      sending = true;
      gate.classList.add('is-sending');
    });
    // Back button after a code: the browser may restore this page from its back/forward cache, still "sending" —
    // start over with an empty, working pad
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      sending = false;
      gate.classList.remove('is-sending', 'is-error');
      if (submitBtn) submitBtn.disabled = false;
      input.value = '';
      render();
    });
  });

  /* ==================== Freunde einladen (referral) ====================
     Only when the page carries a share block (theme setting jb_vip_referral, snippets/jb-vip-form).
     Capturing the inviter is assets/jb-ref.js's job (loaded on every page by layout/theme.liquid, the password page
     included): a #ref / ?ref link is kept on this device as localStorage 'jbVipRef:v1' — the last link opened wins —
     and the code goes on the cart as _jb_ref. Here it only goes out with a signup, as the Klaviyo properties
     jb_vip_referred_by / jb_vip_referred_at. After a signup, the person's OWN code is made from their e-mail —
     first 10 hex of SHA-256("jbvip26:" + e-mail in lower case), upper case — so the same e-mail gives the same
     code in every browser, and the dashboard finds the inviter by hashing the e-mails the same way.
     Own-link signups and the purchase check are sorted out there, not here. */

  var REF_KEY = 'jbVipRef:v1';
  var REF_RE = /^[0-9A-F]{10}$/;
  var refOn = !!document.querySelector('[data-jb-vip-share]');

  // the inviter's code for a signup now — read at submit time, so a link opened in another tab still counts
  function jbVipRefInviter() {
    try {
      var o = JSON.parse(localStorage.getItem(REF_KEY) || 'null');
      if (o && REF_RE.test(o.c)) return o.c;
    } catch (e) {}
    return '';
  }

  function jbVipRefCodeFor(addr) {
    try {
      if (!window.crypto || !window.crypto.subtle || !window.TextEncoder) return Promise.resolve('');
      return window.crypto.subtle.digest('SHA-256', new TextEncoder().encode('jbvip26:' + String(addr).trim().toLowerCase()))
        .then(function (buf) {
          var b = new Uint8Array(buf);
          var hex = '';
          for (var i = 0; i < 5; i++) hex += (b[i] < 16 ? '0' : '') + b[i].toString(16);
          return hex.toUpperCase();
        }, function () { return ''; });
    } catch (e) {
      return Promise.resolve('');
    }
  }

  function jbVipShareTrack(channel) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'jb_vip_share', channel: channel });
    } catch (err) {}
    try {
      if (window.klaviyo && typeof window.klaviyo.push === 'function') {
        window.klaviyo.push(['track', 'VIP 2026 Share', { channel: channel }]);
      }
    } catch (err) {}
  }

  // after a successful signup: fill and show every share block on the page (hero, CTA …)
  function jbVipShareShow(addr) {
    if (!refOn) return;
    jbVipRefCodeFor(addr).then(function (code) {
      if (!REF_RE.test(code)) return; // no Web Crypto → no block; the signup itself is unaffected
      Array.prototype.forEach.call(document.querySelectorAll('[data-jb-vip-share]'), function (box) {
        var end = parseInt(box.getAttribute('data-end'), 10) || 0;
        if (end && Date.now() / 1000 >= end) return;
        var url = (box.getAttribute('data-url') || location.origin + '/pages/juicebox-limited-vip-2026') + '#ref=' + code;
        // the launch text from 10:00 on (the phase attribute flips on an open page too), else the pre-launch one
        var stage = box.closest('[data-jb-vip-phase]');
        var isOpen = !!stage && stage.getAttribute('data-jb-vip-phase') === 'open';
        var msg = (isOpen && box.getAttribute('data-message-open')) || box.getAttribute('data-message') || '';
        msg = msg.indexOf('[link]') !== -1 ? msg.split('[link]').join(url) : (msg ? msg + ' ' : '') + url;
        var wa = box.querySelector('[data-jb-vip-share-wa]');
        if (wa) wa.href = 'https://wa.me/?text=' + encodeURIComponent(msg);
        box.setAttribute('data-link', url);
        box.hidden = false;
      });
    });
  }

  if (refOn) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-jb-vip-share]'), function (box) {
      var wa = box.querySelector('[data-jb-vip-share-wa]');
      var copy = box.querySelector('[data-jb-vip-share-copy]');
      var copyLabel = box.querySelector('[data-jb-vip-share-copy-label]');
      if (wa) wa.addEventListener('click', function () { jbVipShareTrack('whatsapp'); });
      if (!copy || !copyLabel) return;
      var label = copyLabel.textContent;
      var resetTimer = null;
      function copied() {
        copyLabel.textContent = copy.getAttribute('data-done') || label;
        copy.classList.add('is-done');
        clearTimeout(resetTimer);
        resetTimer = setTimeout(function () { copyLabel.textContent = label; copy.classList.remove('is-done'); }, 2500);
      }
      // older browsers: copy through a throw-away field (16px, so iOS does not zoom); last resort shows the link
      function copyBySelection(link) {
        var ta = document.createElement('textarea');
        ta.value = link;
        ta.setAttribute('readonly', '');
        ta.style.cssText = 'position:fixed;top:0;left:-9999px;font-size:16px;opacity:0';
        document.body.appendChild(ta);
        var done = false;
        try {
          ta.select();
          ta.setSelectionRange(0, link.length);
          done = !!(document.execCommand && document.execCommand('copy'));
        } catch (e) {}
        document.body.removeChild(ta);
        if (done) copied();
        else { try { window.prompt('Dein Link zum Kopieren:', link); } catch (e) {} }
      }
      copy.addEventListener('click', function () {
        var link = box.getAttribute('data-link');
        if (!link) return;
        jbVipShareTrack('copy');
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(link).then(copied, function () { copyBySelection(link); });
        } else {
          copyBySelection(link);
        }
      });
    });
  }

  /* ==================== signup forms ==================== */

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var allForms = Array.prototype.slice.call(document.querySelectorAll('[data-jb-vip-form]'));
  // The one signup of this page view, shared by every instance: all success panels show after it, and the
  // "Light oder Original?" buttons in any of them update the same profile.
  var jbVipSignup = { email: '', optin: 'no', linie: '', cfg: null };

  function jbVipMarkAllDone(except) {
    allForms.forEach(function (other) {
      other.setAttribute('data-jb-vip-optin', jbVipSignup.optin);
      if (other === except) return;
      var f = other.querySelector('.jb-vip-form__form');
      var ok = other.querySelector('[data-jb-vip-success]');
      if (f) f.hidden = true;
      if (ok) ok.hidden = false;
      other.setAttribute('data-jb-vip-done', '');
    });
  }

  function jbVipBindForm(root) {
    var form = root.querySelector('.jb-vip-form__form');
    var email = root.querySelector('input[name="email"]');
    var hp = root.querySelector('input[name="jb_vip_website"]');
    var optin = root.querySelector('input[name="jb_vip_mehr_lose"]');
    var btn = root.querySelector('.jb-vip-form__submit');
    var ctaTexts = Array.prototype.slice.call(root.querySelectorAll('[data-jb-vip-cta-text]'));
    var errBox = root.querySelector('[data-jb-vip-error]');
    var errText = root.querySelector('[data-jb-vip-error-text]');
    var retry = root.querySelector('[data-jb-vip-retry]');
    var success = root.querySelector('[data-jb-vip-success]');
    if (!form || !email || !btn) return;

    var cfg = {
      company: root.getAttribute('data-company') || '',
      list: root.getAttribute('data-list') || '',
      revision: root.getAttribute('data-revision') || '2024-10-15',
      source: root.getAttribute('data-source') || 'Limited VIP 2026 Landingpage',
      campaign: root.getAttribute('data-campaign') || 'limited-vip-2026-10',
      consent: root.getAttribute('data-consent') || '',
      instance: root.getAttribute('data-jb-vip-instance') || ''
    };
    var idleLabels = ctaTexts.map(function (el) { return el.textContent; });
    var busyLabel = root.getAttribute('data-busy') || '';
    var renderedAt = Date.now();
    var sending = false;

    function setError(msg, canRetry) {
      if (!errBox) return;
      if (!msg) { errBox.hidden = true; if (retry) retry.hidden = true; return; }
      if (errText) errText.textContent = msg;
      if (retry) retry.hidden = !canRetry;
      errBox.hidden = false;
    }

    function setBusy(on) {
      sending = on;
      btn.disabled = on;
      btn.setAttribute('aria-busy', on ? 'true' : 'false');
      root.classList.toggle('is-sending', on);
      ctaTexts.forEach(function (el, i) { el.textContent = on && busyLabel ? busyLabel : idleLabels[i]; });
    }

    email.addEventListener('input', function () {
      if (email.getAttribute('aria-invalid') === 'true' && EMAIL_RE.test(email.value.trim())) {
        email.removeAttribute('aria-invalid');
        setError('');
      }
    });

    if (retry) {
      retry.addEventListener('click', function () { setError(''); try { email.focus(); } catch (e) {} });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (sending) return;
      // Bots: filled honeypot, or a submit faster than a person can type. Drop silently.
      if (hp && hp.value) return;
      if (Date.now() - renderedAt < 1500) return;
      if (!cfg.list || !cfg.company) { setError(root.getAttribute('data-error-generic') || 'Formular inaktiv.', false); return; }

      var addr = (email.value || '').trim();
      if (!EMAIL_RE.test(addr)) {
        email.setAttribute('aria-invalid', 'true');
        setError(root.getAttribute('data-error-email') || 'Bitte gib eine gültige E-Mail-Adresse ein.', false);
        try { email.focus(); } catch (err) {}
        return;
      }
      email.removeAttribute('aria-invalid');
      setError('');

      var attrs = {
        email: addr,
        properties: {
          jb_vip_2026_10: 'yes',
          jb_launch_campaign: cfg.campaign,
          jb_launch_consent_version: cfg.consent,
          jb_vip_signup_date: new Date().toISOString(),
          jb_vip_signup_instance: cfg.instance
        }
        // No `subscriptions` block: revision 2024-10-15 rejects it on this endpoint
        // ("not a valid field for the resource 'profile'"); the list relationship is
        // what subscribes the profile, same as snippets/wm-tippen-form.liquid.
      };
      // "mehr Lose" checkbox: sent only when ticked, so a later signup without it never overwrites an earlier yes.
      var mehrLose = !!(optin && optin.checked);
      if (mehrLose) attrs.properties.jb_vip_mehr_lose = 'yes';
      // Freunde einladen: who invited this person (the last #ref link opened on this device). Sent only when there
      // is one, so a later signup without a link never wipes an earlier invitation.
      var inviter = refOn ? jbVipRefInviter() : '';
      if (inviter) {
        attrs.properties.jb_vip_referred_by = inviter;
        attrs.properties.jb_vip_referred_at = new Date().toISOString();
      }
      var payload = {
        data: {
          type: 'subscription',
          attributes: { custom_source: cfg.source, profile: { data: { type: 'profile', attributes: attrs } } },
          relationships: { list: { data: { type: 'list', id: cfg.list } } }
        }
      };

      setBusy(true);
      var ctrl = ('AbortController' in window) ? new AbortController() : null;
      var to = ctrl ? setTimeout(function () { ctrl.abort(); }, 12000) : null;

      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(cfg.company), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': cfg.revision },
        body: JSON.stringify(payload),
        signal: ctrl ? ctrl.signal : undefined
      }).then(function (res) {
        if (to) clearTimeout(to);
        if (res.status === 202 || res.ok) {
          jbVipSignup.email = addr;
          jbVipSignup.optin = mehrLose ? 'yes' : 'no';
          jbVipSignup.cfg = cfg;
          root.setAttribute('data-jb-vip-optin', jbVipSignup.optin);
          form.hidden = true;
          root.setAttribute('data-jb-vip-done', '');
          if (success) {
            success.hidden = false;
            try { success.focus({ preventScroll: true }); } catch (err) {}
            if (!reduceMotion) { try { success.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (err) {} }
          }
          jbVipMarkAllDone(root);
          jbVipShareShow(addr);
          try {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ event: 'jb_vip_signup', campaign: cfg.campaign, instance: cfg.instance, mehr_lose: jbVipSignup.optin });
          } catch (err) {}
          try {
            if (window.klaviyo && typeof window.klaviyo.push === 'function') {
              window.klaviyo.push(['track', 'VIP 2026 Signup', { instance: cfg.instance, campaign: cfg.campaign, mehr_lose: jbVipSignup.optin }]);
            }
          } catch (err) {}
          return;
        }
        return res.text().then(function (t) { throw new Error('HTTP ' + res.status + ' ' + t); });
      }).catch(function () {
        if (to) clearTimeout(to);
        setBusy(false);
        setError(root.getAttribute('data-error-generic') || 'Das hat gerade nicht geklappt. Bitte versuch es gleich noch einmal.', true);
      });
    });
  }

  allForms.forEach(jbVipBindForm);

  /* "Light oder Original?" on the success panel → profile property jb_vip_linie (light | original | beides).
     client/profiles only updates the profile: no list re-add, so no list-triggered flow fires again.
     The block lives inside the answer card, so only people who ticked "mehr Lose" see it; every copy on the page
     (hero, CTA) shows the same state. A saved choice replaces the buttons with a success line; "Ändern" brings
     them back and the last saved click wins. */

  var linieSending = false;
  var linieBoxes = Array.prototype.slice.call(document.querySelectorAll('[data-jb-vip-linie-box]'));

  function jbVipLinieLabel(value) {
    var b = document.querySelector('[data-jb-vip-linie="' + value + '"]');
    return b ? b.textContent.trim() : value;
  }

  // state: 'done' (saved → success line), 'ask' (buttons), 'retry' (buttons + error)
  function jbVipLinieState(state, focusBox) {
    linieBoxes.forEach(function (box) {
      var ask = box.querySelector('[data-jb-vip-linie-ask]');
      var done = box.querySelector('[data-jb-vip-linie-done]');
      var doneText = box.querySelector('[data-jb-vip-linie-done-text]');
      var err = box.querySelector('[data-jb-vip-linie-error]');
      Array.prototype.forEach.call(box.querySelectorAll('[data-jb-vip-linie]'), function (b) {
        b.setAttribute('aria-pressed', b.getAttribute('data-jb-vip-linie') === jbVipSignup.linie ? 'true' : 'false');
      });
      var isDone = state === 'done' && !!jbVipSignup.linie;
      if (doneText && isDone) {
        doneText.textContent = (box.getAttribute('data-thanks') || '').replace('[linie]', jbVipLinieLabel(jbVipSignup.linie));
      }
      if (done) done.hidden = !isDone;
      if (ask) ask.hidden = isDone;
      if (err) {
        err.textContent = state === 'retry' ? (box.getAttribute('data-retry') || '') : '';
        err.hidden = state !== 'retry';
      }
      box.classList.toggle('is-done', isDone);
    });
    if (!focusBox) return;
    try {
      if (state === 'done') focusBox.querySelector('[data-jb-vip-linie-done]').focus({ preventScroll: true });
      else (focusBox.querySelector('[aria-pressed="true"]') || focusBox.querySelector('[data-jb-vip-linie]')).focus({ preventScroll: true });
    } catch (err) {}
  }

  linieBoxes.forEach(function (box) {
    box.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      if (e.target.closest('[data-jb-vip-linie-change]')) { jbVipLinieState('ask', box); return; }
      var btn = e.target.closest('[data-jb-vip-linie]');
      if (!btn || !box.contains(btn)) return;
      var value = btn.getAttribute('data-jb-vip-linie');
      var c = jbVipSignup.cfg;
      if (!jbVipSignup.email || !c || linieSending) return;
      if (value === jbVipSignup.linie) { jbVipLinieState('done', box); return; }

      linieSending = true;
      btn.setAttribute('aria-busy', 'true');
      var ctrl = ('AbortController' in window) ? new AbortController() : null;
      var to = ctrl ? setTimeout(function () { ctrl.abort(); }, 12000) : null;

      fetch('https://a.klaviyo.com/client/profiles/?company_id=' + encodeURIComponent(c.company), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': c.revision },
        body: JSON.stringify({ data: { type: 'profile', attributes: { email: jbVipSignup.email, properties: { jb_vip_linie: value } } } }),
        signal: ctrl ? ctrl.signal : undefined
      }).then(function (res) {
        if (!(res.status === 202 || res.ok)) throw new Error('HTTP ' + res.status);
        jbVipSignup.linie = value;
        jbVipLinieState('done', box);
        try {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: 'jb_vip_linie', campaign: c.campaign, linie: value });
        } catch (err) {}
      }).catch(function () {
        jbVipLinieState('retry', null);
      }).then(function () {
        if (to) clearTimeout(to);
        linieSending = false;
        btn.removeAttribute('aria-busy');
      });
    });
  });
})();
